import { useState, useEffect, useCallback } from 'react';
import { 
  shouldShowSessionWarning, 
  getSessionTimeRemaining, 
  isSessionExpired,
  extendSession,
  clearAuthData,
  isAuthenticated,
  TIMEOUT_CONFIG
} from '../helpers/auth-helper';

export const useSessionWarning = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isWarningShown, setIsWarningShown] = useState(false);

  const handleContinueSession = useCallback(() => {
    extendSession();
    setShowWarning(false);
    setIsWarningShown(false);
    // Reset time remaining based on user role
    const userRole = sessionStorage.getItem('roleName') || 'DEFAULT';
    const timeoutHours = TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS[userRole] || TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS.DEFAULT;
    setTimeRemaining(timeoutHours * 60 * 60); // Reset based on role's timeout in seconds
  }, []);

  const handleLogout = useCallback(() => {
    clearAuthData();
    setShowWarning(false);
    setIsWarningShown(false);
    window.location.href = '/home';
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      return;
    }

    const checkSession = () => {
      const remaining = getSessionTimeRemaining();
      setTimeRemaining(remaining);

      // Check if session is expired
      if (isSessionExpired()) {
        //console.log('⏰ Session expired after inactivity, logging out...');
        handleLogout();
        return;
      }

      // Show warning if needed and not already shown
      if (shouldShowSessionWarning() && !isWarningShown && !showWarning) {
        const remainingMin = Math.floor(remaining / 60);
        //console.log(`⚠️ Showing session warning modal (${remainingMin}m ${remaining % 60}s remaining)`);
        setShowWarning(true);
        setIsWarningShown(true);
      }
    };

    // Check immediately
    checkSession();

    // Set up interval to check every 5 seconds (optimized for performance)
    const interval = setInterval(checkSession, 5000);

    return () => clearInterval(interval);
  }, [isWarningShown, showWarning, handleLogout]);

  // Reset warning shown flag when user extends session
  useEffect(() => {
    if (timeRemaining > TIMEOUT_CONFIG.SESSION_WARNING_MINUTES * 60) {
      setIsWarningShown(false);
    }
  }, [timeRemaining]);

  return {
    showWarning,
    timeRemaining: Math.min(timeRemaining, TIMEOUT_CONFIG.SESSION_WARNING_MINUTES * 60), // Cap at warning threshold
    handleContinueSession,
    handleLogout
  };
};
